import { useCallback, useMemo } from "react";
import { useSupportEmail } from "@/hooks/useSupportEmail";
import {
  isIntercomReady,
  openSupportChat,
  type OpenSupportChatOptions,
  buildMailtoFallback,
} from "@/lib/intercom";

/**
 * Branded WhatsApp click-to-chat link for support. Centralised here so every
 * support CTA across the app points at the same number.
 */
export const WHATSAPP_SUPPORT_URL = "https://wa.me/message/WCFE4GFJKZZUK1";

/** A toll-free support phone number for a given region. */
export interface SupportPhoneNumber {
  /** Region discriminator — also drives the i18n country label + testid. */
  region: "uk" | "us";
  /** E.164 dialable number for the `tel:` href. */
  e164: string;
  /** Human-formatted number for display. */
  display: string;
  /** Flag emoji (decorative). */
  flag: string;
}

/**
 * Toll-free customer support numbers, centralised so every surface renders
 * the same digits.
 */
export const SUPPORT_PHONE_NUMBERS: SupportPhoneNumber[] = [
  { region: "uk", e164: "+448008708010", display: "+44 800 870 8010", flag: "🇬🇧" },
  { region: "us", e164: "+18665208081", display: "+1 866 520 8081", flag: "🇺🇸" },
];

export interface UseSupportOpenOptions {
  prefill?: string;
  context?: Record<string, unknown>;
  subject?: string;
  eventName?: string;
  emailOverride?: string;
  visibleFallback?: boolean;
}

export interface UseSupportResult {
  /** The configured support email (from /config/platform). */
  email: string;
  /** Open Messenger; returns visibility outcome. UI callers opt into visible fallback. */
  openChat: (opts?: UseSupportOpenOptions) => Promise<boolean>;
  emailHref: (opts?: UseSupportOpenOptions) => string;
  /** True when the Intercom widget has loaded and is ready to display. */
  isReady: boolean;
  /** Branded WhatsApp click-to-chat support link. */
  whatsappUrl: string;
  /** Toll-free customer support phone numbers. */
  phoneNumbers: SupportPhoneNumber[];
}

/**
 * Single hook that every support CTA across the app uses. Centralizes:
 *   - the platform-configured support email (no more hardcoded addresses),
 *   - Messenger opening and email fallback destinations,
 *   - context metadata pass-through for Fin AI.
 */
export function useSupport(): UseSupportResult {
  const email = useSupportEmail();

  const openChat = useCallback(
    (opts?: UseSupportOpenOptions) => {
      const target = opts?.emailOverride || email;
      const fallback = `mailto:${target}`;
      const payload: OpenSupportChatOptions = {
        mailtoFallback: fallback,
        prefill: opts?.prefill,
        context: opts?.context,
        subject: opts?.subject,
        eventName: opts?.eventName,
        visibleFallback: opts?.visibleFallback,
      };
      return openSupportChat(payload);
    },
    [email],
  );

  return useMemo(
    () => ({
      email,
      openChat,
      emailHref: (opts?: UseSupportOpenOptions) => buildMailtoFallback({
        mailtoFallback: `mailto:${opts?.emailOverride || email}`,
        subject: opts?.subject,
        prefill: opts?.prefill,
      }),
      isReady: isIntercomReady(),
      whatsappUrl: WHATSAPP_SUPPORT_URL,
      phoneNumbers: SUPPORT_PHONE_NUMBERS,
    }),
    [email, openChat],
  );
}
